// Pure form-state <-> display helpers for the Execution panel. These convert
// only at the UI boundary — the form state (and therefore the schema, the
// validation and the provider's API mapping) is unchanged: execution times
// stay a comma string of hours, fee/spread values stay raw fractions, and
// notional scaling stays the (bool, type-string) pair.

// ---------------------------------------------------------------------------
// Execution-time hour list  <->  "10,15" comma string
// ---------------------------------------------------------------------------

// Parse a stored comma string (e.g. "15", "10,15", tolerant of "10:00") into a
// sorted, de-duplicated list of valid hours-of-day (0-23).
export const parseHours = (value: string | undefined | null): number[] => {
  if (!value) return [];
  const hours = value
    .split(',')
    .map((part) => parseInt(part.trim(), 10))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 23);
  return Array.from(new Set(hours)).sort((a, b) => a - b);
};

// Serialize ticked hours back to the stored comma string, ascending.
export const serializeHours = (hours: number[]): string =>
  Array.from(new Set(hours))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 23)
    .sort((a, b) => a - b)
    .join(',');

// ---------------------------------------------------------------------------
// Unit-native value  <->  raw fraction string
//   scale = display / raw   (bps -> 10000, % -> 100, absolute -> 1)
// ---------------------------------------------------------------------------

// Trim float noise (e.g. 2.4999999999996) and drop trailing zeros.
const clean = (n: number, decimals: number): string => parseFloat(n.toFixed(decimals)).toString();

// Raw fraction string -> unit-native display string (e.g. 0.00025 -> "2.5").
export const rawToUnit = (raw: string | undefined | null, scale: number, decimals = 6): string => {
  if (raw === undefined || raw === null || raw.trim() === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return clean(n * scale, decimals);
};

// Unit-native display string -> raw fraction string (e.g. "2.5" -> "0.00025").
export const unitToRaw = (display: string | undefined | null, scale: number): string => {
  if (display === undefined || display === null || display.trim() === '') return '';
  const n = Number(display);
  if (!Number.isFinite(n)) return display;
  return clean(n / scale, 12);
};

// ---------------------------------------------------------------------------
// Notional scaling  <->  (optionsNotionalScaled, notionalScalingType)
// ---------------------------------------------------------------------------

export type NotionalScalingOption = 'None' | 'PV' | 'HWM';

// Derive the single-select option from the two stored fields.
export const notionalOptionFromFields = (
  scaled: boolean,
  type: string | undefined | null,
): NotionalScalingOption => {
  if (!scaled) return 'None';
  return type === 'HWM' ? 'HWM' : 'PV';
};

// Map the selected option back onto the two stored fields.
export const notionalFieldsFromOption = (
  option: NotionalScalingOption,
): { optionsNotionalScaled: boolean; notionalScalingType: string } => {
  switch (option) {
    case 'PV':
      return { optionsNotionalScaled: true, notionalScalingType: 'PV' };
    case 'HWM':
      return { optionsNotionalScaled: true, notionalScalingType: 'HWM' };
    case 'None':
    default:
      return { optionsNotionalScaled: false, notionalScalingType: '' };
  }
};
